import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { BootScreen } from './components/ui';
import { useSession } from './session';
import { useT } from './i18n';
import { LoginPage, SignupPage } from './pages/AuthPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { ProjectShell } from './pages/ProjectShell';
import { OverviewPage } from './pages/OverviewPage';
import { PlayersPage } from './pages/PlayersPage';
import { ConfigPage } from './pages/ConfigPage';
import { LeaderboardsPage } from './pages/LeaderboardsPage';
import { MultiplayerPage } from './pages/MultiplayerPage';
import { HostingPage } from './pages/HostingPage';
import { PaymentsPage } from './pages/PaymentsPage';
import { KeysPage } from './pages/KeysPage';
import { AuditPage } from './pages/AuditPage';
import { TransferPage } from './pages/TransferPage';
import { MembersPage } from './pages/MembersPage';
import { OwnerOnlyPage } from './components/OwnerOnly';
import { TransferIncomingPage } from './pages/TransferIncomingPage';
import { TRANSFER_INCOMING_PATH } from './projectTransfer';
import { SignInMethodsPage } from './pages/SignInMethodsPage';
import { ForgotPasswordPage, ResetPasswordPage } from './pages/AccountRecovery';
import { VerifyEmailPage } from './pages/VerifyEmail';
import { ResetDonePage } from './pages/ResetDone';
import { RotateSecretKeysPage, SecretKeysPage } from './pages/AccountSecretKeys';
import { RESET_DONE_PATH, ROTATE_PATH, SECRET_KEYS_PATH } from './secretKeys';
import { FunctionsPage } from './pages/FunctionsPage';
import { SharePage } from './pages/SharePage';
import { MailPage } from './pages/mail/MailPage';
import { AccountPage } from './pages/AccountPage';
import { MoneyShell } from './pages/money/MoneyShell';
import { EarningsPage } from './pages/money/EarningsPage';
import { WithdrawPage } from './pages/money/WithdrawPage';
import { PayeePage } from './pages/money/PayeePage';
import { PrepaidPage } from './pages/money/PrepaidPage';
import { UsagePage } from './pages/money/UsagePage';
import { PasskeysPage } from './pages/money/PasskeysPage';
import { ConfirmWithdrawalPage } from './pages/money/ConfirmWithdrawalPage';
import { SalesReportPage } from './pages/money/SalesReportPage';
import { ThreeDSecurePage } from './pages/money/ThreeDSecurePage';
import { AdsListPage } from './pages/money/AdsListPage';
import { PlayEarnPage } from './pages/money/PlayEarnPage';
import { AdsNewPage } from './pages/money/AdsNewPage';
import { AdDetailPage } from './pages/money/AdDetailPage';
import { StaffAdsReviewPage } from './pages/StaffAdsReviewPage';
import { STAFF_ADS_PATH } from './selfAds';
import { isMoneyNavHidden, isNavHidden, moneyHomePath } from './launchHidden';
import { OAuthCallbackPage } from './pages/OAuthCallback';
import { CALLBACK_PATH } from './devOAuth';
import { afterLoginPath } from './authForms';
import { McpAuthorizePage } from './pages/McpAuthorize';
import { MCP_AUTHORIZE_PATH } from './mcpAuthorize';
import { CliAuthorizePage } from './pages/CliAuthorize';
import { CLI_AUTHORIZE_PATH } from './cliAuthorize';
import { CliDevicePage } from './pages/CliDevice';
import { CLI_DEVICE_PATH } from './cliDevice';
import { OLD_CONNECT_PATH } from './connectPage';
import { SignupOpened } from './components/SignupOpened';
import { GuidePage } from './pages/GuidePage';
import { GUIDE_PATH } from './guidePage';
import { ConfirmActionPage } from './pages/ConfirmAction';
import { CONFIRM_ACTION_PATH } from './pendingAction';
import { ENTER_KEY_PATH } from './enterKey';
import { EnterKeyPage } from './pages/EnterKeyPage';
import { UPLOAD_PATH } from './uploadFiles';
import { UploadPage } from './pages/UploadPage';
import { AccountSignInPage, SignInCallbackPage } from './pages/AccountSignIn';
import { PlayerPage } from './pages/PlayerPage';
import { CreatorStartPage } from './pages/CreatorStart';
import { PLAYER_PATH, PLAYER_PROFILE_PATH, PLAYER_SIGNIN_PATH, readSavedMode, SIGNIN_CALLBACK_PATH, START_CREATING_PATH } from './oneAccount';

const savedMode = () => {
  try {
    return readSavedMode(window.localStorage);
  } catch {
    return null;
  }
};

export function App() {
  const { developer, account, oneAccount, ready } = useSession();
  const location = useLocation();
  const { t } = useT();
  // 起動したときだけ「最後に選んだ方」を開く（1 つのアカウント・2026-10-07 console/one-account）。WHY 最初の 1 回だけか: いつも見ると、
  // Player から Creator へ切り替えて / を開いたときに Player へ戻されてしまう。値は描画の中で決め、外すのは effect（StrictMode の 2 回の描画で結果が変わらない）
  const [applySavedMode, setApplySavedMode] = useState(true);
  useEffect(() => {
    if (ready) setApplySavedMode(false);
  }, [ready]);
  // Google / Apple / GitHub からの戻り（2026-09-27 creator/dev-oauth-ui）。ログインの有無・起動の読み込みに関係なく、いつも同じ位置で出す。
  // WHY: 下の 2 つの Routes のどちらかに置くと、ログインが済んだ瞬間に別の木へ作り直され、画面の途中の状態（takeover の案内）が消える。
  //   起動の読み込み（ready）も待たない: URL の code / error をすぐ読んで消したい（pages/OAuthCallback.tsx）
  if (location.pathname === CALLBACK_PATH) return <OAuthCallbackPage />;
  if (!ready) {
    return <BootScreen label={t('app.starting')} />;
  }
  // 1 つのアカウント（旗 oneAccount・2026-10-07 console/one-account）: account からの戻り。ログインの有無に関係なく同じ画面
  // （/oauth/callback と同じ理由 = 入った瞬間に別の木へ作り直されない）。起動の読み込みは待つ（前の作り手の鍵の読み込みと入れ違わない）
  if (oneAccount.enabled && location.pathname === SIGNIN_CALLBACK_PATH) return <SignInCallbackPage />;
  if (!developer && !account) {
    return (
      <Routes>
        {/* 登録とログインは別の画面・別のフォーム（パスワード管理が新しいアカウントと既存のログインを見分けられるように・authForms.ts） */}
        {/* 旗 oneAccount が on なら、ログインは account で行う（pages/AccountSignIn.tsx）。登録も account（誰でもアカウントを作れる）=
            /signup はログインへ。来た印（from: /signup）を持たせ、入った後に「招待コードを入れる」画面へ（サーバーの invite_code_required の signupUrl） */}
        <Route path="/login" element={oneAccount.enabled ? <AccountSignInPage /> : <LoginPage />} />
        {/* 開かれた数（components/SignupOpened.tsx・2026-10-18 ops/gaps-activity・広告のテスト用）: どちらの形でも最初の search の invite・src を 1 回だけ知らせる */}
        <Route path="/signup" element={<SignupOpened>{oneAccount.enabled ? <Navigate to="/login" replace state={{ from: '/signup' }} /> : <SignupPage />}</SignupOpened>} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        {/* 再設定の完了（外したログイン方法・残っているサーバーの鍵・pages/ResetDone.tsx）。再設定は全セッションを失効させるので、ふつうはこちらの木で開く */}
        <Route path={RESET_DONE_PATH} element={<ResetDonePage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        {/* ログイン後に元の画面へ戻れるよう、行き先を state で持っていく */}
        {/* search と hash も持っていく: 出金の確認はトークンを #token に置く（SEC-ACCESS-23）ので、pathname だけだとログイン後に消える */}
        <Route path="*" element={<Navigate to="/login" replace state={{ from: location.pathname + location.search + location.hash }} />} />
      </Routes>
    );
  }
  if (!developer) {
    // アカウントだけ（作り手の中身が無い人・旗 on のときだけ起きる）: Player の画面と「招待コードを入れる」画面だけ。
    // 作り手の画面へのリンク（/cli/device など）で来たら招待コードの画面へ。作り手を始めた後に元の画面へ戻す（state.from）
    return (
      <Routes>
        <Route path={PLAYER_PATH} element={<PlayerPage />} />
        <Route path={PLAYER_PROFILE_PATH} element={<PlayerPage tab="profile" />} />
        <Route path={PLAYER_SIGNIN_PATH} element={<PlayerPage tab="sign-in" />} />
        <Route path={START_CREATING_PATH} element={<CreatorStartPage />} />
        <Route path="/" element={<Navigate to={savedMode() === 'creator' ? START_CREATING_PATH : PLAYER_PATH} replace />} />
        <Route path="/login" element={<Navigate to={PLAYER_PATH} replace />} />
        <Route path="/signup" element={<Navigate to={START_CREATING_PATH} replace />} />
        <Route path="*" element={<Navigate to={START_CREATING_PATH} replace state={{ from: location.pathname + location.search + location.hash }} />} />
      </Routes>
    );
  }
  if (applySavedMode && oneAccount.enabled && location.pathname === '/' && savedMode() === 'player') return <Navigate to={PLAYER_PATH} replace />;
  return (
    <Routes>
      <Route path="/" element={<ProjectsPage />} />
      {/* Player の画面（旗 oneAccount のときだけ・pages/PlayerPage.tsx）。作り手もここで遊ぶ側のアカウントを見る（鍵は kas_ だけ） */}
      {oneAccount.enabled && <Route path={PLAYER_PATH} element={<PlayerPage />} />}
      {/* Player の画面のタブ（2026-10-08 console/account-pages・account のページと同じ 3 つ） */}
      {oneAccount.enabled && <Route path={PLAYER_PROFILE_PATH} element={<PlayerPage tab="profile" />} />}
      {oneAccount.enabled && <Route path={PLAYER_SIGNIN_PATH} element={<PlayerPage tab="sign-in" />} />}
      <Route path="/login" element={<Navigate to={(location.state as { from?: string } | null)?.from ?? '/'} replace />} />
      {/* 登録の直後も、ログインと同じく元の画面（state.from）へ戻す（2026-10-04: Claude からの連携の途中で登録した人が連携の画面に戻れなかった） */}
      <Route path="/signup" element={<Navigate to={afterLoginPath((location.state as { from?: unknown } | null)?.from)} replace />} />
      {/* メールのリンクはログイン中でも開かれる（確認・再設定）。ログイン中の再設定は完了時にこの端末もログアウトする */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path={RESET_DONE_PATH} element={<ResetDonePage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      {/* アカウントの設定（閉鎖・pages/AccountPage.tsx）。お金の画面とは分ける（閉鎖はアカウントの操作） */}
      <Route path="/account" element={<AccountPage />} />
      {/* AI エージェント（MCP）の接続の許可（pages/McpAuthorize.tsx・2026-09-28 creator/mcp-dashboard-login）。ログイン必須:
          ログイン前は下の木の catch-all が `?request=` ごと state.from に入れて /login へ送り、ログイン後にここへ戻る */}
      <Route path={MCP_AUTHORIZE_PATH} element={<McpAuthorizePage />} />
      {/* 「この端末の Kumo CLI を許可」（xkumo connect / xkumo login・pages/CliAuthorize.tsx・2026-09-28 creator/connect）。ログイン必須:
          ログイン前に開かれたら下の木の catch-all が /login へ移し、クエリ（port・s・challenge）ごと state.from に入れて戻す */}
      <Route path={CLI_AUTHORIZE_PATH} element={<CliAuthorizePage />} />
      {/* デバイスコードでのログイン（/cli/device?code=XXXX-XXXX・pages/CliDevice.tsx・2026-10-07 console/device-login）。ログイン必須:
          ログイン前に開かれたら下の木の catch-all が /login へ移し、?code= ごと state.from に入れて戻す（/cli/authorize と同じ） */}
      <Route path={CLI_DEVICE_PATH} element={<CliDevicePage />} />
      {/* /connect は前に配った URL のためにガイドへ移すだけ（2026-10-07 founder「これは使用法に入れればよいよね」= 1 行は使い方ガイドの始め方 1 に出す） */}
      <Route path={OLD_CONNECT_PATH} element={<Navigate to={GUIDE_PATH} replace />} />
      {/* 使い方ガイド（pages/GuidePage.tsx・2026-10-07 console/guide-page）。トップバーの「つなぐ」の隣とホームから。ログイン必須（中のリンクは残高・つなぐの画面） */}
      <Route path={GUIDE_PATH} element={<GuidePage />} />
      {/* AI エージェントの本番の変更をリンクで確認（/confirm/:id・pages/ConfirmAction.tsx・2026-09-28 creator/confirm-link）。ログイン必須:
          ログイン前は下の木の catch-all が state.from に入れて /login へ送り、ログイン後にここへ戻る。
          /confirm/withdrawal（お金の画面・下）とは固定の区切りの方が優先されるので重ならない（id は kpa_… だけ） */}
      <Route path={CONFIRM_ACTION_PATH} element={<ConfirmActionPage />} />
      {/* AI がチャットのボタンで開く「キーを 1 つ入れるだけの小さな画面」（2026-10-04 creator/chat-secrets・pages/EnterKeyPage.tsx） */}
      <Route path={ENTER_KEY_PATH} element={<EnterKeyPage />} />
      {/* AI がチャットのボタン / リンクで開く「ゲームのファイルを入れるだけの小さな画面」（2026-10-04 creator/web-upload・pages/UploadPage.tsx） */}
      <Route path={UPLOAD_PATH} element={<UploadPage />} />
      {/* サーバーの鍵（全プロジェクトの sk_）の一覧とまとめて作り直す（pages/AccountSecretKeys.tsx・2026-09-28 creator/dashboard-pass）。
          通知メール・再設定の完了の画面のボタンの行き先。ログイン必須（ログイン前は下の木の catch-all が state.from に入れて戻す） */}
      <Route path={SECRET_KEYS_PATH} element={<SecretKeysPage />} />
      <Route path={ROTATE_PATH} element={<RotateSecretKeysPage />} />
      {/* プロジェクトを受け取る（招待のメールのボタンの行き先・2026-10-21 core/handoff-1a）。ログイン前は下の * が /login へ送り、ログインの後にここへ戻る */}
      <Route path={TRANSFER_INCOMING_PATH} element={<TransferIncomingPage />} />
      {/* お金（クリエイター単位・pages/money/MoneyShell.tsx）。売上・出金・受け取りの登録は URL にモード（live / test）を持つ */}
      {/* 売上と出金（payouts）は最初の公開で見せない（2026-09-29 creator/launch-money・launchHidden.ts の MONEY_NAV_FEATURES）。
          古いリンク・ブックマーク・メールのリンクで開かれたら前払い残高へ戻す（ナビから消すだけだと URL を打てば開けてしまう。
          サーバーも Core が 403 feature_unavailable で閉じている = 開いても何も読めない画面になるだけ）。一覧から外せば元のページが出る */}
      <Route path="/money" element={<MoneyShell />}>
        <Route index element={<Navigate to={moneyHomePath().replace('/money/', '')} replace />} />
        <Route path=":mode/earnings" element={moneyPage('earnings', <EarningsPage />)} />
        <Route path=":mode/sales" element={moneyPage('sales', <SalesReportPage />)} />
        <Route path=":mode/three-d-secure" element={<ThreeDSecurePage />} />
        <Route path=":mode/withdraw" element={moneyPage('withdraw', <WithdrawPage />)} />
        <Route path=":mode/get-paid" element={moneyPage('get-paid', <PayeePage />)} />
        <Route path="prepaid" element={<PrepaidPage />} />
        <Route path="usage" element={<UsagePage />} />
        <Route path="passkeys" element={moneyPage('passkeys', <PasskeysPage />)} />
        {/* 自分のゲームの広告（2026-10-22 money/self-serve-ads-dash・selfAds.ts）。フラグ off のサーバーでは一覧が「まだ使えません」を出す（ナビは MoneyShell が隠す） */}
        {/* 「初日から稼げる」の稼ぎ（2026-10-26 money/earn-build・playEarn.ts）。見せない人（サーバーが 404）には「まだ使えません」・ナビは MoneyShell が隠す */}
        <Route path="play-earnings" element={<PlayEarnPage />} />
        <Route path="ads" element={<AdsListPage />} />
        <Route path="ads/new" element={<AdsNewPage />} />
        <Route path="ads/:id" element={<AdDetailPage />} />
      </Route>
      {/* 運営の確認（自分のゲームの広告・pages/StaffAdsReviewPage.tsx）。運営の鍵を画面で打つ（作り手のセッションでは通らない）。作り手のナビには出さない */}
      <Route path={STAFF_ADS_PATH} element={<StaffAdsReviewPage />} />
      {/* 出金の確認（ダッシュボードの出金と MCP の出金依頼の両方が来る）。トークンは #token（サーバーのログに残らない）。
          /:token は旧い形（サーバーの confirmUrl がまだこの形）で、ページが #token の形へ置き換える（pages/money/ConfirmWithdrawalPage.tsx）。
          出金（payouts）を見せない間は前払い残高へ（上と同じ） */}
      <Route path="/confirm/withdrawal" element={moneyPage('withdraw', <ConfirmWithdrawalPage />)} />
      <Route path="/confirm/withdrawal/:token" element={moneyPage('withdraw', <ConfirmWithdrawalPage />)} />
      <Route path="/p/:projectId/:env" element={<ProjectShell />}>
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<OverviewPage />} />
        <Route path="players" element={<PlayersPage />} />
        <Route path="config" element={<ConfigPage />} />
        <Route path="sign-in" element={<OwnerOnlyPage><SignInMethodsPage /></OwnerOnlyPage>} />
        {/* ランキングと実績は最初の公開で見せない（launchHidden.ts・founder 2026-09-27）。古いリンク・ブックマークで開かれたら概要へ戻す
            （ナビから消すだけだと URL を打てば開けてしまう）。一覧から外せば元のページが出る */}
        <Route path="leaderboards" element={isNavHidden('leaderboards') ? <Navigate to="../overview" replace /> : <LeaderboardsPage />} />
        <Route path="multiplayer" element={<MultiplayerPage />} />
        <Route path="hosting" element={<HostingPage />} />
        <Route path="functions" element={<FunctionsPage />} />
        <Route path="share" element={<SharePage />} />
        {/* メール配信も最初の公開で見せない（2026-09-29 creator/mail-close・Kumo 管理の決定）。古いリンクで開かれたら概要へ戻す */}
        <Route path="mail" element={isNavHidden('mail') ? <Navigate to="../overview" replace /> : <OwnerOnlyPage><MailPage /></OwnerOnlyPage>} />
        {/* 課金（利用者への販売）も最初の公開で見せない（2026-09-29 creator/launch-money・ランキングと同じ扱い） */}
        <Route path="payments" element={isNavHidden('payments') ? <Navigate to="../overview" replace /> : <OwnerOnlyPage><PaymentsPage /></OwnerOnlyPage>} />
        <Route path="keys" element={<OwnerOnlyPage><KeysPage /></OwnerOnlyPage>} />
        <Route path="audit" element={<AuditPage />} />
        {/* 共同作業者（2026-10-22 core/handoff-1b）。持ち主だけの画面（ログイン方法・メール・販売・キー・人に渡す）は OwnerOnlyPage で包む =
            共同作業者が URL を打って開いても「持ち主だけが使えます」（ナビからも消える = ProjectShell の navFor）。守りはサーバーの表 */}
        <Route path="members" element={<MembersPage />} />
        {/* 人に渡す（2026-10-21 core/handoff-1a・第 1a 段 = 持ち主を移すだけ） */}
        <Route path="transfer" element={<OwnerOnlyPage><TransferPage /></OwnerOnlyPage>} />
      </Route>
      <Route path="/p/:projectId" element={<Navigate to="development/overview" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/** お金の画面のうち見せない区切り（isMoneyNavHidden）なら前払い残高へ戻す。見せるならそのまま */
function moneyPage(section: string, page: ReactNode): ReactNode {
  return isMoneyNavHidden(section) ? <Navigate to="/money/prepaid" replace /> : page;
}
